// ==========================================
// FILE: src/components/features/PetWalker/PetWalkerPage.js
// Display page showing all registered pet walkers
// ==========================================
import React, { useState, useEffect } from 'react';
import { MapPin, Phone, DollarSign, Clock, Mail, Star, MessageCircle } from 'lucide-react';
import { useAPI } from '../../../hooks/useAPI.js';
import PetWalkerRegistration from './PetWalkerRegistration.js';
import Pagination from '../../common/Pagination.js';

const PetWalkerPage = ({ user }) => {
  const { apiCall } = useAPI();
  const [petWalkers, setPetWalkers] = useState([]);
  const [userIsWalker, setUserIsWalker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showRegistration, setShowRegistration] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const walkersPerPage = 25;

  useEffect(() => {
    loadPetWalkers();
    checkIfUserIsWalker();
  }, []);

  const loadPetWalkers = async () => {
    setLoading(true);
    try {
      const data = await apiCall('/petwalker/all');
      setPetWalkers(data.petwalkers || []);
    } catch (err) {
      console.error('Error loading pet walkers:', err);
      setPetWalkers([]);
    } finally {
      setLoading(false);
    }
  };

  const checkIfUserIsWalker = async () => {
    try {
      const data = await apiCall('/petwalker/status');
      setUserIsWalker(data.is_petwalker || false);
    } catch (err) {
      console.error('Error checking walker status:', err);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRegistrationSuccess = () => {
    loadPetWalkers();
    checkIfUserIsWalker();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pet walkers...</p>
        </div>
      </div>
    );
  }

  // Calculate pagination
  const totalPages = Math.ceil(petWalkers.length / walkersPerPage);
  const startIndex = (currentPage - 1) * walkersPerPage;
  const endIndex = startIndex + walkersPerPage;
  const currentWalkers = petWalkers.slice(startIndex, endIndex);

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl shadow-lg p-6 sm:p-8 md:p-12 mb-8">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
          🐕 Find a Pet Walker
        </h1>
        <p className="text-base sm:text-lg md:text-xl opacity-90 mb-6">
          Connect with trusted pet care professionals in your area
        </p>

        {/* Registration CTA */}
        {!userIsWalker && (
          <button
            onClick={() => setShowRegistration(true)}
            className="bg-white text-purple-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
          >
            ➕ Become a Pet Walker
          </button>
        )}
        {userIsWalker && (
          <div className="bg-white/20 border border-white/30 rounded-lg px-4 py-3 inline-flex items-center gap-2">
            <span className="text-xl">✓</span>
            <span className="font-semibold">You're registered as a pet walker</span>
          </div>
        )}
      </div>

      {/* Stats Bar */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
        <p className="text-purple-900 font-semibold">
          {petWalkers.length} pet walker{petWalkers.length !== 1 ? 's' : ''} available
          {petWalkers.length > 0 && ` • Showing ${startIndex + 1}-${Math.min(endIndex, petWalkers.length)}`}
        </p>
      </div>

      {/* Pet Walkers Grid */}
      {currentWalkers.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <div className="text-6xl mb-4">🐾</div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">No Pet Walkers Yet</h3>
          <p className="text-gray-600 mb-6">Be the first to register as a pet walker!</p>
          {!userIsWalker && (
            <button
              onClick={() => setShowRegistration(true)}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-semibold"
            >
              Register Now
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {currentWalkers.map((walker) => (
              <PetWalkerCard key={walker.id} walker={walker} currentUser={user} />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {/* Registration Modal */}
      {showRegistration && (
        <PetWalkerRegistration
          user={user}
          onClose={() => setShowRegistration(false)}
          onSuccess={handleRegistrationSuccess}
        />
      )}
    </div>
  );
};

// ==========================================
// Pet Walker Card Component
// ==========================================
const PetWalkerCard = ({ walker, currentUser }) => {
  const getAvailabilityText = () => {
    if (!walker.availability) return 'Contact for availability';
    
    const days = Object.entries(walker.availability)
      .filter(([_, available]) => available)
      .map(([day, _]) => day.substring(0, 3));
    
    if (days.length === 7) return 'Available 7 days a week';
    if (days.length === 0) return 'Contact for availability';
    return days.join(', ');
  };

  const getServicesText = () => {
    if (!walker.services) return [];
    
    const serviceMap = {
      dog_walking: 'Dog Walking',
      pet_sitting: 'Pet Sitting',
      overnight_care: 'Overnight Care',
      dog_training: 'Dog Training'
    };
    
    return Object.entries(walker.services)
      .filter(([_, offered]) => offered)
      .map(([key, _]) => serviceMap[key]);
  };

  const services = getServicesText();

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition">
      {/* Header with Profile */}
      <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-6 text-white">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center flex-shrink-0">
            {walker.user.profile_picture ? (
              <img
                src={walker.user.profile_picture}
                alt={walker.user.username}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <span className="text-purple-600 font-bold text-2xl">
                {walker.user.username[0].toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-bold truncate">{walker.user.username}</h3>
            {walker.experience_years && (
              <p className="text-sm opacity-90">
                {walker.experience_years} {walker.experience_years === 1 ? 'year' : 'years'} experience
              </p>
            )}
          </div>
        </div>

        {/* Hourly Rate */}
        {walker.hourly_rate && (
          <div className="bg-white/20 rounded-lg px-3 py-2 inline-flex items-center gap-2">
            <DollarSign size={16} />
            <span className="font-semibold">${walker.hourly_rate}/hour</span>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-6 space-y-4">
        {/* Bio */}
        <div>
          <p className="text-gray-700 text-sm line-clamp-4">{walker.bio}</p>
        </div>

        {/* Services */}
        {services.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Services</p>
            <div className="flex flex-wrap gap-2">
              {services.map(service => (
                <span
                  key={service}
                  className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold"
                >
                  {service}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Location */}
        {walker.service_area && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <MapPin size={16} className="flex-shrink-0" />
            <span className="truncate">{walker.service_area}</span>
          </div>
        )}

        {/* Availability */}
        <div className="flex items-start gap-2 text-sm text-gray-600">
          <Clock size={16} className="flex-shrink-0 mt-0.5" />
          <span>{getAvailabilityText()}</span>
        </div>

        {/* Certifications */}
        {walker.certifications && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-blue-900 mb-1">Certifications</p>
            <p className="text-xs text-blue-700">{walker.certifications}</p>
          </div>
        )}

        {/* Contact Info */}
        <div className="border-t pt-4 space-y-2">
          {walker.phone && (
            <a
              href={`tel:${walker.phone}`}
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-purple-600 transition"
            >
              <Phone size={16} />
              <span>{walker.phone}</span>
            </a>
          )}
          {walker.user.email && (
            <a
              href={`mailto:${walker.user.email}`}
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-purple-600 transition"
            >
              <Mail size={16} />
              <span className="truncate">{walker.user.email}</span>
            </a>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-semibold text-sm">
            <MessageCircle size={16} />
            Message
          </button>
          {walker.user.id !== currentUser.id && (
            <button className="px-4 py-2 border-2 border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition font-semibold text-sm">
              <Star size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PetWalkerPage;